import { useMutation, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { Dialog } from "@fronterahealth/frontera-ui-components";

import {
  type GetOrganizationsOutput,
  type OrganizationSummary,
  basecampOrganizationsRetrieve,
  updateTemplate,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface AssignOrgsDialogProps {
  templateId: number;
  templateName: string;
  currentOrgs: readonly OrganizationSummary[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function AssignOrgsDialog({
  templateId,
  templateName,
  currentOrgs,
  open,
  onClose,
  onSaved,
}: AssignOrgsDialogProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(currentOrgs?.map((o) => o.id) ?? []));
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset selection when dialog opens
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setSelectedIds(new Set(currentOrgs?.map((o) => o.id) ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSearchQuery("");
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const { data: orgsData } = useQuery({
    queryKey: ["all-orgs"],
    queryFn: () => basecampOrganizationsRetrieve(),
    enabled: open,
  });

  const allOrgs: GetOrganizationsOutput[] =
    orgsData?.status === 200 ? (Array.isArray(orgsData.data) ? orgsData.data : [orgsData.data]) : [];

  const filteredOrgs = searchQuery.trim()
    ? allOrgs.filter((o) => o.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : allOrgs;

  const saveMutation = useMutation({
    mutationFn: () =>
      updateTemplate({
        template_id: templateId,
        organization_ids: Array.from(selectedIds).map(Number),
      }),
    onSuccess: () => {
      notifySuccess("Organizations updated");
      onSaved();
      onClose();
    },
    onError: () => notifyError("Failed to update organizations"),
  });

  const toggleOrg = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedOrgs = allOrgs.filter((o) => selectedIds.has(Number(o.id)));

  if (!open) return null;

  return (
    <Dialog
      title={`Edit Organizations for "${templateName}"`}
      open={open}
      setOpen={(v) => {
        if (!v) onClose();
      }}
      primaryButton={{
        text: "Save",
        onClick: () => saveMutation.mutate(),
        disabled: saveMutation.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      size="sm"
    >
      <div className="flex max-h-[60vh] flex-col gap-4">
        <p className="text-sm text-text-secondary">
          This template will only be available to the organizations selected below. Updating this list will replace the
          template&apos;s existing organization access.
        </p>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            className="flex items-center gap-2 rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-primary hover:bg-bg-default"
            onClick={() => (dropdownOpen ? closeDropdown() : setDropdownOpen(true))}
          >
            <span>Select organizations</span>
            {selectedIds.size > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rio-600 text-xs text-white">
                {selectedIds.size}
              </span>
            )}
            <span className="ml-1 text-text-tertiary">▾</span>
          </button>
          {dropdownOpen && (
            <div className="absolute z-50 mt-1 w-64 rounded-md border border-border-subtle bg-bg-surface shadow-md">
              <div className="sticky top-0 border-b border-border-subtle bg-bg-surface p-2">
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search organizations…"
                  className="w-full rounded border border-border-medium px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500"
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filteredOrgs.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-text-tertiary">No matches</div>
                ) : (
                  filteredOrgs.map((org) => {
                    const checked = selectedIds.has(Number(org.id));
                    return (
                      <label
                        key={org.id}
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-bg-default"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border-medium text-rio-600"
                          checked={checked}
                          onChange={() => toggleOrg(Number(org.id))}
                        />
                        <span className={clsx("truncate", checked ? "text-text-primary" : "text-text-secondary")}>
                          {org.name}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <p className="mb-1 text-sm text-text-secondary">Selected organizations ({selectedOrgs.length})</p>
          <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border-subtle">
            {selectedOrgs.length > 0 ? (
              selectedOrgs.map((org) => (
                <div
                  key={org.id}
                  className="border-b border-border-subtle px-3 py-2 text-sm text-text-primary last:border-b-0"
                >
                  {org.name}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-text-tertiary">No organizations selected</div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
